import { FC, Fragment } from 'react';
import { AntTooltip } from '../../components/common/AntTooltip';
import { LOCAL_ID_PREFIX } from '../../constants';
import { GMedication } from '../../store/db';
import { ItemId, LAB_INVESTIGATIONS, MEDICINES } from './GenieConfiguration';
import { FilterData, FilterType, GUIItem, Item } from './ListGenieItems';

export const filtersObj: {
    [k in FilterType]: {
        id: FilterType;
        label: string;
        getData: (array: Item[]) => Item[];
        getLength: (array: Item[]) => number;
    };
} = {
    all: {
        id: 'all',
        label: 'All',
        getData: (array: Item[]) => array,
        getLength: (array: Item[]) => array.length,
    },
    uncoded: {
        id: 'uncoded',
        label: 'Uncoded',
        getData: (array: Item[]) => {
            const elements = array.filter((t) => (t as GUIItem).currentItemStatus === 'uncoded');
            return elements;
        },
        getLength: (array: Item[]) => {
            const elements = array.filter(
                (t) => t.id.startsWith(LOCAL_ID_PREFIX) && !(t as GMedication).med_cat,
            );
            return elements.length;
        },
    },
    coded: {
        id: 'coded',
        label: 'Coded',
        getData: (array: Item[]) => {
            const elements = array.filter((t) => (t as GUIItem).currentItemStatus === 'coded');
            return elements;
        },
        getLength: (array: Item[]) => {
            const elements = array.filter((t) => !t.id.startsWith(LOCAL_ID_PREFIX));
            return elements.length;
        },
    },
    nad: {
        id: 'nad',
        label: 'Not a',
        getData: (array: Item[]) => {
            const elements = array.filter((t) => (t as GUIItem).currentItemStatus === 'nad');
            return elements;
        },
        getLength: (array: Item[]) => {
            const elements = array.filter((t) => (t as GMedication).med_cat);
            return elements.length;
        },
    },
};

type CodeStatusFilterBoxType = {
    id: string;
    isLoading: boolean;
    label: string;
    onClick: () => void;
    isSelected: boolean;
    count?: number;
    itemId: ItemId;
    name: string;
};

const CodeStatusFilterBox: FC<CodeStatusFilterBoxType> = ({
    id,
    isLoading,
    label,
    onClick,
    isSelected,
    count,
    itemId,
    name,
}) => {
    const tooltipContent: { [k: string]: string } = {
        [MEDICINES]: 'Medicines with missing information about Forms or Generic.',
        [LAB_INVESTIGATIONS]: 'Lab Investigations with missing information about Remarks.',
    };
    return (
        <div>
            <div
                className={`px-16 py-8 space-y-4 border cursor-pointer rounded-4 text-13 min-w-160 ${
                    isSelected
                        ? `border-actionPrimary ${
                              id === 'uncoded'
                                  ? 'text-darwin-status-warning-500'
                                  : 'text-actionPrimary'
                          }`
                        : `border-neutral-7 ${
                              id === 'uncoded' ? 'text-darwin-status-warning-500' : 'text-neutral-1'
                          }`
                }`}
                onClick={onClick}
            >
                <div className="flex items-center justify-between space-x-8">
                    <p className={`${isSelected ? 'font-700' : 'font-500'}`}>
                        {label} {name}
                    </p>
                    {id === 'uncoded' && (
                        <AntTooltip placement="bottom" content={tooltipContent[itemId]}>
                            <svg
                                className="w-14 h-14 fill-darwin-accent-bright-yellow-800"
                                viewBox="0 0 14 15"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M7 14.75C10.8555 14.75 14 11.6328 14 7.75C14 3.89453 10.8555 0.75 7 0.75C3.11719 0.75 0 3.89453 0 7.75C0 11.6328 3.11719 14.75 7 14.75ZM7 4.25C7.35547 4.25 7.65625 4.55078 7.65625 4.90625V7.96875C7.65625 8.35156 7.35547 8.625 7 8.625C6.61719 8.625 6.34375 8.35156 6.34375 7.96875V4.90625C6.34375 4.55078 6.61719 4.25 7 4.25ZM7.875 10.375C7.875 10.8672 7.46484 11.25 7 11.25C6.50781 11.25 6.125 10.8672 6.125 10.375C6.125 9.91016 6.50781 9.5 7 9.5C7.46484 9.5 7.875 9.91016 7.875 10.375Z" />
                            </svg>
                        </AntTooltip>
                    )}
                </div>
                <div className="flex items-center justify-between space-x-8">
                    {isLoading ? (
                        <div className="w-full h-32 shimmer"></div>
                    ) : !count ? (
                        <div className="w-full leading-7 text-18 min-h-32">-</div>
                    ) : (
                        <Fragment>
                            {count && (
                                <p className={`text-21 ${isSelected ? 'font-700' : 'font-500'}`}>
                                    {count}
                                </p>
                            )}
                        </Fragment>
                    )}
                </div>
            </div>

            {isSelected && (
                <div className="w-16 h-16 flex items-center justify-center mx-auto">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 320 512"
                        className="w-16 h-16 fill-actionPrimary"
                    >
                        <path d="M31.3 192h257.3c17.8 0 26.7 21.5 14.1 34.1L174.1 354.8c-7.8 7.8-20.5 7.8-28.3 0L17.2 226.1C4.6 213.5 13.5 192 31.3 192z" />
                    </svg>
                </div>
            )}
        </div>
    );
};

const CodeStatusFilters: FC<{
    data?: FilterData;
    isLoading: boolean;
    codeStatusFilter: FilterType;
    setFilter: (pf: FilterType) => void;
    itemId: ItemId;
    name: string;
}> = ({ data, isLoading, codeStatusFilter, setFilter, itemId, name }): JSX.Element => {
    const filtersArray = Object.entries(filtersObj);
    if (itemId !== MEDICINES) filtersArray.pop();
    return (
        <div className="flex-1 flex mt-12 mb-4 space-x-16 px-16">
            {filtersArray.map(([key, value]) => (
                <CodeStatusFilterBox
                    key={key}
                    id={value.id}
                    label={value.label}
                    onClick={() => setFilter(key as FilterType)}
                    isSelected={codeStatusFilter === key}
                    isLoading={isLoading}
                    count={data && data[key as FilterType]?.count}
                    itemId={itemId}
                    name={name}
                />
            ))}
        </div>
    );
};

export default CodeStatusFilters;
